"use client";

import { c as _c } from "react/compiler-runtime";
import { IntentPrefetchLink } from "@/components/navigation/IntentPrefetchLink";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/Collapsible";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/Sheet";
import { textVariants } from "@/components/ui/Text";
import { useIsMobile } from "@/hooks/use-mobile";
import { Slot } from "@radix-ui/react-slot";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ExternalLinkIcon, PanelLeft } from "lucide-react";
import * as React from "react";
import cn from "twsx";
const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_KEYBOARD_SHORTCUT = "\\";
type SidebarContextValue = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};
const SidebarContext = React.createContext<SidebarContextValue | null>(null);
export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}
export function SidebarProvider(t0) {
  const $ = _c(18);
  const {
    defaultOpen: t1,
    children
  } = t0;
  const defaultOpen = t1 === undefined ? true : t1;
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [open, setOpenState] = React.useState(defaultOpen);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = nextOpen => {
      setOpenState(nextOpen);
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${nextOpen}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const setOpen = t2;
  let t3;
  if ($[1] !== isMobile || $[2] !== open) {
    t3 = () => {
      if (isMobile) {
        setOpenMobile(_temp);
        return;
      }
      setOpen(!open);
    };
    $[1] = isMobile;
    $[2] = open;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const toggleSidebar = t3;
  let t4;
  let t5;
  if ($[4] !== toggleSidebar) {
    t4 = () => {
      const handleKeyDown = event => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t5 = [toggleSidebar];
    $[4] = toggleSidebar;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  React.useEffect(t4, t5);
  const t6 = open ? "expanded" : "collapsed";
  let t7;
  if ($[7] !== isMobile || $[8] !== open || $[9] !== openMobile || $[10] !== t6 || $[11] !== toggleSidebar) {
    t7 = {
      state: t6,
      open,
      setOpen,
      openMobile,
      setOpenMobile,
      isMobile,
      toggleSidebar
    };
    $[7] = isMobile;
    $[8] = open;
    $[9] = openMobile;
    $[10] = t6;
    $[11] = toggleSidebar;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const value = t7;
  let t8;
  if ($[13] !== children) {
    t8 = <TooltipProvider delayDuration={0}>{children}</TooltipProvider>;
    $[13] = children;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8 || $[16] !== value) {
    t9 = <SidebarContext.Provider value={value}>{t8}</SidebarContext.Provider>;
    $[15] = t8;
    $[16] = value;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}
function _temp(current) {
  return !current;
}
export function Sidebar(t0) {
  const $ = _c(16);
  const {
    children,
    className
  } = t0;
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile
  } = useSidebar();
  if (isMobile) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <VisuallyHidden asChild={true}><SheetTitle>Workspace navigation</SheetTitle></VisuallyHidden>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== setOpenMobile) {
      t2 = event => {
        if (event.target instanceof Element && event.target.closest("a[href]")) {
          setOpenMobile(false);
        }
      };
      $[1] = setOpenMobile;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== children || $[4] !== t2) {
      t3 = <SheetContent side="left" data-sidebar="sidebar" size="nav" tone="sidebar" showClose={false}>{t1}<div className="flex h-full flex-col px-1 py-2" onClick={t2}>{children}</div></SheetContent>;
      $[3] = children;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== openMobile || $[7] !== setOpenMobile || $[8] !== t3) {
      t4 = <Sheet open={openMobile} onOpenChange={setOpenMobile}>{t3}</Sheet>;
      $[6] = openMobile;
      $[7] = setOpenMobile;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t1;
  if ($[10] !== className) {
    t1 = cn("hidden h-full w-sidebar shrink-0 flex-col overflow-hidden bg-sidebar-bg px-1 py-2 text-sidebar-text md:flex", "transition-size duration-200 ease-out", "data-[state=collapsed]:w-0 data-[state=collapsed]:px-0", className);
    $[10] = className;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== children || $[13] !== state || $[14] !== t1) {
    t2 = <aside data-sidebar="sidebar" data-state={state} aria-label="Workspace navigation" className={t1}>{children}</aside>;
    $[12] = children;
    $[13] = state;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}
export function SidebarHeader(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("mb-2.5 flex h-8 shrink-0 items-center justify-between gap-1 pr-0.5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div data-sidebar="header" className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function SidebarBody(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto overflow-x-hidden overscroll-contain", "[scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:transparent_transparent]", "hover:[scrollbar-color:rgb(255_255_255/0.2)_transparent]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <nav data-sidebar="content" className={t1}>{children}</nav>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function SidebarFooter(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("mt-2 flex shrink-0 flex-col gap-px border-t border-sidebar-border pt-2 pb-safe-bottom", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div data-sidebar="footer" className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function SidebarGroup(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-w-0 flex-col gap-px", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
const itemClass = cn(textVariants({
  size: "md",
  weight: "medium",
  tone: "inherit"
}), "group/sidebar-item flex h-7 w-full min-w-0 items-center gap-2 rounded-control px-2 text-left leading-4 outline-none", "text-sidebar-text transition-colors duration-100 hover:bg-sidebar-hover hover:text-sidebar-text-strong", "focus-visible:ring-1 focus-visible:ring-sidebar-icon", "data-[active=true]:bg-sidebar-active data-[active=true]:text-sidebar-text-strong", "has-[[data-navigation-pending=true]]:bg-sidebar-active has-[[data-navigation-pending=true]]:text-sidebar-text-strong", "disabled:pointer-events-none disabled:opacity-disabled", "[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-sidebar-icon data-[active=true]:[&_svg]:text-sidebar-text-strong");
type SidebarItemContentProps = {
  leading?: React.ReactNode;
  icon?: React.ComponentType<{
    className?: string;
  }> | null;
  label: React.ReactNode;
  count?: number | null;
  countTone?: "default" | "danger";
  trailing?: React.ReactNode;
};
function SidebarItemContent(t0) {
  const $ = _c(13);
  const {
    leading,
    icon: Icon,
    label,
    count,
    countTone: t1,
    trailing
  } = t0;
  const countTone = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== Icon) {
    t2 = Icon && <Icon />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <span className="min-w-0 flex-1 truncate">{label}</span>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== count || $[5] !== countTone) {
    t4 = count != null && count > 0 && <span className={cn(textVariants({
      size: "sm",
      tone: "inherit",
      numeric: true
    }), "shrink-0", countTone === "danger" ? "flex h-4.5 items-center rounded-full bg-red-hover px-1.5 font-medium text-white" : "text-sidebar-icon")}>{count}</span>;
    $[4] = count;
    $[5] = countTone;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== leading || $[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== trailing) {
    t5 = <>{leading}{t2}{t3}{t4}{trailing}</>;
    $[7] = leading;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = trailing;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
type SidebarItemProps = SidebarItemContentProps & Omit<React.ComponentProps<"button">, "children"> & {
  href?: string | null;
  external?: boolean;
  active?: boolean;
  inset?: boolean;
};
export function SidebarItem(t0) {
  const $ = _c(40);
  let className;
  let count;
  let countTone;
  let href;
  let icon;
  let label;
  let leading;
  let props;
  let t1;
  let t2;
  let t3;
  let trailing;
  if ($[0] !== t0) {
    ({
      href,
      external: t1,
      active: t2,
      inset: t3,
      leading,
      icon,
      label,
      count,
      countTone,
      trailing,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = count;
    $[3] = countTone;
    $[4] = href;
    $[5] = icon;
    $[6] = label;
    $[7] = leading;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = trailing;
  } else {
    className = $[1];
    count = $[2];
    countTone = $[3];
    href = $[4];
    icon = $[5];
    label = $[6];
    leading = $[7];
    props = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    trailing = $[12];
  }
  const external = t1 === undefined ? false : t1;
  const active = t2 === undefined ? false : t2;
  const inset = t3 === undefined ? false : t3;
  const t4 = inset && "pl-5.5";
  let t5;
  if ($[13] !== className || $[14] !== t4) {
    t5 = cn(itemClass, t4, className);
    $[13] = className;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const classes = t5;
  let t6;
  if ($[16] !== count || $[17] !== countTone || $[18] !== icon || $[19] !== label || $[20] !== leading || $[21] !== trailing) {
    t6 = <SidebarItemContent leading={leading} icon={icon} label={label} count={count} countTone={countTone} trailing={trailing} />;
    $[16] = count;
    $[17] = countTone;
    $[18] = icon;
    $[19] = label;
    $[20] = leading;
    $[21] = trailing;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const content = t6;
  if (href && external) {
    const t7 = typeof label === "string" ? `${label} (opens in a new tab)` : undefined;
    let t8;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <ExternalLinkIcon aria-hidden="true" className="!size-3.5" />;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== classes || $[25] !== content || $[26] !== href || $[27] !== t7) {
      t9 = <a data-sidebar-item="" href={href} target="_blank" rel="noopener noreferrer" aria-label={t7} className={classes}>{content}{t8}</a>;
      $[24] = classes;
      $[25] = content;
      $[26] = href;
      $[27] = t7;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    return t9;
  }
  if (href) {
    const t7 = active ? "page" : undefined;
    let t8;
    if ($[29] !== active || $[30] !== classes || $[31] !== content || $[32] !== href || $[33] !== t7) {
      t8 = <IntentPrefetchLink data-sidebar-item="" href={href} data-active={active} aria-current={t7} className={classes}>{content}</IntentPrefetchLink>;
      $[29] = active;
      $[30] = classes;
      $[31] = content;
      $[32] = href;
      $[33] = t7;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    return t8;
  }
  let t7;
  if ($[35] !== active || $[36] !== classes || $[37] !== content || $[38] !== props) {
    t7 = <button data-sidebar-item="" type="button" data-active={active} className={classes} {...props}>{content}</button>;
    $[35] = active;
    $[36] = classes;
    $[37] = content;
    $[38] = props;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  return t7;
}
export function SidebarSubList(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("relative ml-4 flex flex-col gap-px pt-px [&_[data-sidebar-item]]:pl-3.75", "before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-sidebar-border");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className={t1}>{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function Caret(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("!size-2 shrink-0 fill-current", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M1.5 2.5h5L4 5.5z" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <svg viewBox="0 0 8 8" aria-hidden="true" className={t1}>{t2}</svg>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
export function SidebarSection(t0) {
  const $ = _c(12);
  const {
    title,
    defaultOpen: t1,
    children
  } = t0;
  const defaultOpen = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(textVariants({
      size: "sm",
      weight: "medium",
      tone: "inherit"
    }), "flex h-7 w-full items-center gap-1.5 rounded-control pl-2.25 pr-1 text-left text-sidebar-icon outline-none", "hover:bg-sidebar-hover focus-visible:ring-1 focus-visible:ring-sidebar-icon");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== title) {
    t3 = <span className="truncate">{title}</span>;
    $[1] = title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Caret className="group-data-[state=closed]/sidebar-section:-rotate-90" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <CollapsibleTrigger className={t2}>{t3}{t4}</CollapsibleTrigger>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children) {
    t6 = <CollapsibleContent>{children}</CollapsibleContent>;
    $[6] = children;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== defaultOpen || $[9] !== t5 || $[10] !== t6) {
    t7 = <Collapsible defaultOpen={defaultOpen} className="group/sidebar-section flex flex-col gap-px">{t5}{t6}</Collapsible>;
    $[8] = defaultOpen;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export function SidebarDisclosure(t0) {
  const $ = _c(14);
  const {
    label,
    icon,
    leading,
    active: t1,
    defaultOpen: t2,
    count,
    countTone,
    children
  } = t0;
  const active = t1 === undefined ? false : t1;
  const defaultOpen = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Caret className="text-sidebar-icon group-data-[state=closed]/sidebar-disclosure:-rotate-90" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== active || $[2] !== count || $[3] !== countTone || $[4] !== icon || $[5] !== label || $[6] !== leading) {
    t4 = <CollapsibleTrigger asChild={true}><SidebarItem active={active} leading={leading} icon={icon} label={label} count={count} countTone={countTone} trailing={t3} /></CollapsibleTrigger>;
    $[1] = active;
    $[2] = count;
    $[3] = countTone;
    $[4] = icon;
    $[5] = label;
    $[6] = leading;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children) {
    t5 = <CollapsibleContent>{children}</CollapsibleContent>;
    $[8] = children;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== defaultOpen || $[11] !== t4 || $[12] !== t5) {
    t6 = <Collapsible defaultOpen={defaultOpen} className="group/sidebar-disclosure">{t4}{t5}</Collapsible>;
    $[10] = defaultOpen;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

// `size="touch"` is the 44px target the mobile top bar uses.
export function SidebarIconButton(t0) {
  const $ = _c(17);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      variant: t1,
      size: t2,
      asChild: t3,
      active: t4,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  const variant = t1 === undefined ? "ghost" : t1;
  const size = t2 === undefined ? "md" : t2;
  const asChild = t3 === undefined ? false : t3;
  const active = t4 === undefined ? false : t4;
  const Comp = asChild ? Slot : "button";
  const t5 = asChild ? undefined : "button";
  let t6;
  if ($[7] !== className || $[8] !== size || $[9] !== variant) {
    t6 = cn("relative flex shrink-0 items-center justify-center rounded-full outline-none [&_svg]:size-4", "transition-colors duration-100 focus-visible:ring-1 focus-visible:ring-sidebar-icon", size === "md" ? "size-7" : "size-11", variant === "primary" && ["border border-navy-500 bg-sidebar-active text-sidebar-text-strong shadow-sidebar-raised", "hover:border-navy-300 hover:bg-navy-500"], variant === "ghost" && ["text-sidebar-icon hover:bg-sidebar-hover hover:text-sidebar-text-strong data-[state=open]:bg-sidebar-hover", "data-[active=true]:bg-sidebar-active data-[active=true]:text-sidebar-text-strong"], className);
    $[7] = className;
    $[8] = size;
    $[9] = variant;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== Comp || $[12] !== active || $[13] !== props || $[14] !== t5 || $[15] !== t6) {
    t7 = <Comp type={t5} data-active={active} className={t6} {...props} />;
    $[11] = Comp;
    $[12] = active;
    $[13] = props;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export function SidebarUnreadDot() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span aria-hidden="true" className="absolute top-1.25 right-1.25 size-1.5 rounded-full bg-accent shadow-sidebar-dot" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function SidebarTrigger(props) {
  const $ = _c(4);
  const {
    toggleSidebar
  } = useSidebar();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PanelLeft />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props || $[2] !== toggleSidebar) {
    t1 = <SidebarIconButton aria-label="Toggle navigation" onClick={toggleSidebar} {...props}>{t0}</SidebarIconButton>;
    $[1] = props;
    $[2] = toggleSidebar;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
export function SidebarMenuButton(t0) {
  const $ = _c(23);
  let className;
  let description;
  let label;
  let leading;
  let props;
  let trailing;
  if ($[0] !== t0) {
    ({
      leading,
      label,
      description,
      trailing,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = description;
    $[3] = label;
    $[4] = leading;
    $[5] = props;
    $[6] = trailing;
  } else {
    className = $[1];
    description = $[2];
    label = $[3];
    leading = $[4];
    props = $[5];
    trailing = $[6];
  }
  let t1;
  if ($[7] !== className) {
    t1 = cn("flex min-h-7 min-w-0 items-center gap-2 rounded-control px-2 py-1 text-left outline-none transition-colors duration-100", "hover:bg-sidebar-hover focus-visible:ring-1 focus-visible:ring-sidebar-icon data-[state=open]:bg-sidebar-hover", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(textVariants({
      size: "md",
      weight: "semibold",
      tone: "inherit",
      truncate: true
    }), "leading-4 text-sidebar-text-strong");
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== label) {
    t3 = <span className={t2}>{label}</span>;
    $[10] = label;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== description) {
    t4 = description && <span className={cn(textVariants({
      size: "xs",
      tone: "inherit",
      truncate: true
    }), "text-sidebar-icon")}>{description}</span>;
    $[12] = description;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <span className="flex min-w-0 flex-1 flex-col">{t3}{t4}</span>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== leading || $[18] !== props || $[19] !== t1 || $[20] !== t5 || $[21] !== trailing) {
    t6 = <button type="button" className={t1} {...props}>{leading}{t5}{trailing}</button>;
    $[17] = leading;
    $[18] = props;
    $[19] = t1;
    $[20] = t5;
    $[21] = trailing;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
}